import get from 'lodash/get';
import set from 'lodash/set';
import camelCase from 'lodash/camelCase';
import BackgroundColor from '../background-color/background-color';

import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
  wrapper,
} from '../../lib/editing-configs';
import {
  backgroundPositionXValues,
  backgroundPositionYValues,
  backgroundSizes,
  buildSpacingInputs,
} from './universal-container-utils';

const isTestEnv = process?.env?.NODE_ENV;
/**
 *
 * @param {*} componentDocument
 * @param {string} fieldLabel
 * @param {Array<'backgroundImage'|'backgroundColor'|'padding'|'margin'|'border'|'width'|'alignment'|'hide'|'jumpLinkId'>} allowedControls allowedControls: List of controls to be shown in the editor
 * @param {string} fieldName
 * @param {{disableGroup: boolean, customLabels: {background: string}}} options
 * @returns
 */
export const getEditingConfig = (
  component,
  fieldLabel = 'Universal Container',
  allowedControls = isTestEnv
    ? ['backgroundColor', 'backgroundImage', 'border', 'margin', 'padding']
    : [],
  fieldName = 'universalContainer',
  options = {
    disableGroup: false,
    customLabels: {
      background: 'Background',
    },
  }
) => {
  const componentDocument = set(
    component,
    `props.${fieldName}.alignment.direction`,
    'column'
  );
  const shouldDisplayCustomPadding =
    get(componentDocument, `props.${fieldName}.padding.type`, 'currency') ===
    'custom';
  const shouldDisplayCustomMargin =
    get(componentDocument, `props.${fieldName}.margin.type`, 'currency') ===
    'custom';

  const paddingType = get(
    componentDocument,
    `props.${fieldName}.padding.type`,
    'currency'
  );

  const marginType = get(
    componentDocument,
    `props.${fieldName}.margin.type`,
    'currency'
  );

  const paddingInputConfig = shouldDisplayCustomPadding
    ? buildSpacingInputs(
        numberInputSpec,
        `${fieldName}.padding.${paddingType}`,
        ['top', 'right', 'left', 'bottom'],
        '(pixels)'
      )
    : buildSpacingInputs(
        stringSelectSpec,
        `${fieldName}.padding.${paddingType}`,
        ['top', 'right', 'left', 'bottom'],
        null,
        !shouldDisplayCustomPadding
      );

  const paddingEditorControls = allowedControls.includes('padding')
    ? [
        group('Padding', [
          stringSelectSpec(
            `${fieldName}.padding.type`,
            'Padding Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...paddingInputConfig,
        ]),
      ]
    : [];

  const marginInputConfig = shouldDisplayCustomMargin
    ? buildSpacingInputs(
        stringInputSpec,
        `${fieldName}.margin.${marginType}`,
        ['top', 'right', 'left', 'bottom'],
        '(pixels)'
      )
    : buildSpacingInputs(
        stringSelectSpec,
        `${fieldName}.margin.${marginType}`,
        ['top', 'right', 'left', 'bottom'],
        null,
        !shouldDisplayCustomMargin
      );

  const marginEditorControls = allowedControls.includes('margin')
    ? [
        group('Margin', [
          stringSelectSpec(
            `${fieldName}.margin.type`,
            'Margin Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...marginInputConfig,
        ]),
      ]
    : [];

  const backgroundImageEditorControls = allowedControls.includes(
    'backgroundImage'
  )
    ? [
        stringInputSpec(
          `${fieldName}.backgroundImageDesktop`,
          'Background Image URL (Desktop)'
        ),
        stringInputSpec(
          `${fieldName}.backgroundImageTablet`,
          'Background Image URL (Tablet)'
        ),
        stringInputSpec(
          `${fieldName}.backgroundImageMobile`,
          'Background Image URL (Mobile)'
        ),
        stringSelectSpec(
          `${fieldName}.backgroundSize`,
          'Background Size',
          backgroundSizes
        ),
        stringSelectSpec(
          `${fieldName}.backgroundPositionX`,
          'Background Position X',
          backgroundPositionXValues
        ),
        stringSelectSpec(
          `${fieldName}.backgroundPositionY`,
          'Background Position X',
          backgroundPositionYValues
        ),
      ]
    : [];

  const backgroundColorEditorControls = allowedControls.includes(
    'backgroundColor'
  )
    ? [...BackgroundColor.getEditingConfig(componentDocument, fieldName)]
    : [];

  const backgroundConfigEditorControls =
    backgroundImageEditorControls.length || backgroundColorEditorControls.length
      ? [
          group(options?.customLabels?.background || 'Background', [
            ...backgroundImageEditorControls,
            ...backgroundColorEditorControls,
          ]),
        ]
      : [];

  const borderColorInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') === 'color'
      ? [
          stringInputSpec(
            `${fieldName}.border.borderColor`,
            'Border Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
          numberInputSpec(
            `${fieldName}.border.borderWidth`,
            'Border Width (eg: "2" - default is 0, change it to a higher value to be visible)'
          ),
        ]
      : [];

  const borderShadowInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') ===
    'shadow'
      ? [
          stringInputSpec(
            `${fieldName}.border.shadowColor`,
            'Drop Shadow Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
        ]
      : [];

  const borderRoundedCurrencyConfig =
    get(
      componentDocument,
      `props.${fieldName}.border.borderRounded.type`,
      'currency'
    ) === 'currency'
      ? [
          stringSelectSpec(
            `${fieldName}.border.borderRounded.currencyValue`,
            'Currency Value',
            [
              { label: 'None', value: '' },
              { label: 'border-radius-1', value: 'border-radius-1' },
              { label: 'border-radius-2', value: 'border-radius-2' },
            ],
            {
              help: 'Defaults to "None"',
            }
          ),
        ]
      : [];

  const borderRoundedCustomConfig =
    get(
      componentDocument,
      `props.${fieldName}.border.borderRounded.type`,
      'currency'
    ) === 'custom'
      ? [
          numberInputSpec(
            `${fieldName}.border.borderRounded.value`,
            'Custom Value'
          ),
        ]
      : [];

  const borderRoundedIndividualConfig =
    get(
      componentDocument,
      `props.${fieldName}.border.borderRounded.type`,
      'currency'
    ) === 'individual'
      ? [
          stringInputSpec(
            `${fieldName}.border.borderRounded.value`,
            'Individual Values',
            {
              help: 'Enter values for each corner separated by a space (eg: "10px 20px 30px 40px")',
            }
          ),
        ]
      : [];

  const currencyOrCustomRoundedBorderConfig = [
    ...borderRoundedCurrencyConfig,
    ...borderRoundedCustomConfig,
    ...borderRoundedIndividualConfig,
  ];

  const borderRoundedInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') !== 'none'
      ? [
          stringSelectSpec(
            `${fieldName}.border.borderRounded.type`,
            'Border Rounded Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
              { label: 'Individual Values', value: 'individual' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...currencyOrCustomRoundedBorderConfig,
        ]
      : [];

  const borderInputConfig = [
    group('Border', [
      stringSelectSpec(`${fieldName}.border.type`, 'Border', [
        { label: 'None', value: 'none' },
        { label: 'Solid Rounded Corners', value: 'rounded' },
        { label: 'Border Line', value: 'color' },
        { label: 'Border Shadow', value: 'shadow' },
      ]),
      ...borderColorInputConfig,
      ...borderShadowInputConfig,
      ...borderRoundedInputConfig,
    ]),
  ];

  const borderEditorControls = allowedControls.includes('border')
    ? [...borderInputConfig]
    : [];

  const widthInputConfig =
    get(componentDocument, `props.${fieldName}.width.type`, 'currency') ===
    'currency'
      ? [
          stringSelectSpec(
            `${fieldName}.width.currency.value`,
            'Currency Value',
            [
              { label: 'None', value: '' },
              { label: 'Width one thirds', value: 'calc(100% / 3)' },
              { label: 'Width two thirds', value: 'calc(2 * 100% / 3)' },
              { label: 'Width 10', value: '10%' },
              { label: 'Width 20', value: '20%' },
              { label: 'Width 25', value: '25%' },
              { label: 'Width 30', value: '30%' },
              { label: 'Width 40', value: '40%' },
              { label: 'Width 50', value: '50%' },
              { label: 'Width 60', value: '60%' },
              { label: 'Width 70', value: '70%' },
              { label: 'Width 75', value: '75%' },
              { label: 'Width 80', value: '80%' },
              { label: 'Width 90', value: '90%' },
              { label: 'Width 100', value: '100%' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
        ]
      : [numberInputSpec(`${fieldName}.width.custom.value`, 'Custom Value')];

  const widthEditorControls = allowedControls.includes('width')
    ? [
        group('Width', [
          stringSelectSpec(
            `${fieldName}.width.type`,
            'Width Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...widthInputConfig,
          boolCheckboxSpec(
            `${fieldName}.width.screenSizes.lg`,
            'Apply Width In Desktop Screens'
          ),
          boolCheckboxSpec(
            `${fieldName}.width.screenSizes.md`,
            'Apply Width In Tablet Screens'
          ),
          boolCheckboxSpec(
            `${fieldName}.width.screenSizes.sm`,
            'Apply Width In Mobile Screens'
          ),
        ]),
      ]
    : [];

  const hideEditorControls = allowedControls.includes('hide')
    ? [
        group('Hide', [
          boolCheckboxSpec(`${fieldName}.hide.lg`, 'Hide In Desktop Screens'),
          boolCheckboxSpec(`${fieldName}.hide.md`, 'Hide In Tablet Screens'),
          boolCheckboxSpec(`${fieldName}.hide.sm`, 'Hide In Mobile Screens'),
        ]),
      ]
    : [];

  const orientation = get(
    componentDocument,
    `props.${fieldName}.alignment.direction`,
    ''
  );

  const alignmentInputConfig = [
    group('Alignment', [
      stringSelectSpec(
        `${fieldName}.alignment.horizontal`,
        orientation === 'column' ? 'Horizontal' : 'Vertical',
        [
          {
            label: orientation === 'column' ? 'Left' : 'Top',
            value: 'flex-start',
          },
          { label: 'Center', value: 'center' },
          {
            label: orientation === 'column' ? 'Right' : 'Bottom',
            value: 'flex-end',
          },
        ]
      ),
      stringSelectSpec(
        `${fieldName}.alignment.vertical`,
        orientation === 'column' ? 'Vertical' : 'Horizontal',
        [
          {
            label: orientation === 'column' ? 'Top' : 'Left',
            value: 'flex-start',
          },
          { label: 'Center', value: 'center' },
          {
            label: orientation === 'column' ? 'Bottom' : 'Right',
            value: 'flex-end',
          },
        ]
      ),
    ]),
  ];

  const alignmentEditorControls = allowedControls.includes('alignment')
    ? [...alignmentInputConfig]
    : [];

  const jumpLinkIdEditorControls = allowedControls.includes('jumpLinkId')
    ? [
        group('Jump Link ID', [
          stringInputSpec(`${fieldName}.jumpLinkId`, 'Jump link ID', {
            help: 'You can specify a unique Jump Link ID for this product card to navigate directly to the spot on the page where this card appears',
          }),
          ...(get(componentDocument, `props.${fieldName}.jumpLinkId`, false)
            ? ['desktop', 'tablet', 'mobile'].map((device) =>
                numberInputSpec(
                  `${fieldName}.${camelCase(`scroll-offset-${device}`)}`,
                  `Scroll offset for ${device}`,
                  {
                    help: `Add additional scroll offset for ${device} devices (use to increase top padding and push content down from location of Jump link ID)`,
                  }
                )
              )
            : []),
        ]),
      ]
    : [];

  const editorPanelOptions = [
    ...widthEditorControls,
    ...alignmentEditorControls,
    ...backgroundConfigEditorControls,
    ...paddingEditorControls,
    ...marginEditorControls,
    ...borderEditorControls,
    ...hideEditorControls,
    ...jumpLinkIdEditorControls,
  ];

  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

export default getEditingConfig;
